import { z } from 'zod';
import {
  countryCodeSchema,
  entityIdSchema,
  isoDateTimeSchema,
  localeSchema,
  safeTextSchema,
  uniqueArraySchema,
} from './common';

/**
 * Rights grant for a content item. Streaming, embedding, linking and ingest are separate delivery
 * permissions; subtitling and adaptation are separate flags (spec: Assumption 9). The window is
 * [startsAt, endsAt) in UTC; `endsAt: null` means open-ended. Only `approved` grants count.
 * Real cross-border enforcement and contracts still require legal review (CONTENT_RIGHTS.md).
 */

export const DELIVERY_METHODS = ['stream', 'embed', 'link', 'ingest'] as const;
export const deliveryMethodSchema = z.enum(DELIVERY_METHODS);
export type DeliveryMethod = z.infer<typeof deliveryMethodSchema>;

export const RIGHTS_REVIEW_STATES = ['pending', 'approved', 'rejected'] as const;

export const rightsGrantFieldsSchema = z.strictObject({
  id: entityIdSchema,
  contentItemId: entityIdSchema,
  deliveryMethods: uniqueArraySchema(deliveryMethodSchema).min(1),
  allowedCountries: uniqueArraySchema(countryCodeSchema).min(1).max(60),
  /** null = no locale restriction; otherwise only these caption/presentation locales. */
  permittedLocales: uniqueArraySchema(localeSchema).min(1).nullable(),
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema.nullable(),
  subtitlePermitted: z.boolean(),
  adaptationPermitted: z.boolean(),
  /** Where the permission is documented (contract reference, licence, internal production). */
  evidence: safeTextSchema(500),
  reviewState: z.enum(RIGHTS_REVIEW_STATES),
});

/** The rights window must not be empty; shared by the stored grant and editorial input. */
export const grantWindowRule: [
  (g: { startsAt: string; endsAt: string | null }) => boolean,
  { message: string; path: string[] },
] = [
  (g) => g.endsAt === null || Date.parse(g.endsAt) > Date.parse(g.startsAt),
  { message: 'endsAt must be after startsAt', path: ['endsAt'] },
];

export const rightsGrantSchema = rightsGrantFieldsSchema.refine(...grantWindowRule);
export type RightsGrant = z.infer<typeof rightsGrantSchema>;
